/**
 * ============================================================================
 * COLOR UTILITIES
 * ============================================================================
 * 
 * Utility functions for color manipulation and conversion
 */

/**
 * Converts HEX color to RGB
 * @param {string} hex - Color in #RRGGBB format
 * @returns {Object} {r, g, b}
 */
export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Converts RGB to HEX
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} Color in #RRGGBB format
 */
export function rgbToHex(r, g, b) {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Lightens a color
 * @param {string} hex - Color in #RRGGBB format
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} Lightened color in #RRGGBB format
 */
export function lightenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const amount = Math.round(2.55 * percent);
  
  const r = Math.min(255, rgb.r + amount);
  const g = Math.min(255, rgb.g + amount);
  const b = Math.min(255, rgb.b + amount);
  
  return rgbToHex(r, g, b);
}

/**
 * Darkens a color
 * @param {string} hex - Color in #RRGGBB format
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} Darkened color in #RRGGBB format
 */
export function darkenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const amount = Math.round(2.55 * percent);
  
  const r = Math.max(0, rgb.r - amount);
  const g = Math.max(0, rgb.g - amount);
  const b = Math.max(0, rgb.b - amount);
  
  return rgbToHex(r, g, b);
}

/**
 * Generates a transparent version of a color
 * @param {string} hex - Color in #RRGGBB format
 * @param {number} alpha - Opacity (0-1)
 * @returns {string} Color in rgba() format
 */
export function withOpacity(hex, alpha) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Calculates whether to use white or black text on a color
 * @param {string} bgColor - Background color in #RRGGBB format
 * @returns {string} '#ffffff' or '#000000'
 */
export function getContrastText(bgColor) {
  const rgb = hexToRgb(bgColor);
  if (!rgb) return '#ffffff';
  
  // Calculate relative luminance (WCAG)
  const luminance = ((0.299 * rgb.r) + (0.587 * rgb.g) + (0.114 * rgb.b)) / 255;
  
  // If dark (luminance < 0.5), use white; if light, use black
  return luminance > 0.5 ? '#000000' : '#ffffff';
}
